import * as THREE from "three";
import { scene } from "../core";
import { VISUAL_CONFIG } from "@shared/config";

/**
 * Creates an improved rocket mesh with better details and effects
 */
export function createRocketMesh(x: number, y: number): THREE.Group {
  const rocketGroup = new THREE.Group();
  rocketGroup.name = "rocketMesh";

  // Create materials with more depth and detail
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: VISUAL_CONFIG.COLORS.ROCKET,
    emissive: VISUAL_CONFIG.COLORS.ROCKET_EMISSIVE,
    emissiveIntensity: 0.7,
    roughness: 0.3,
    metalness: 0.7,
  });

  const detailMaterial = new THREE.MeshStandardMaterial({
    color: 0x888888,
    emissive: 0x444444,
    emissiveIntensity: 0.5,
    roughness: 0.4,
    metalness: 0.8,
  });

  // Rocket body with better shape - slightly tapered cylinder
  const bodyGeometry = new THREE.CylinderGeometry(
    0.05, // top radius
    0.065, // bottom radius - slightly wider
    0.3, // height
    16, // radial segments
    3, // height segments
    false // open-ended
  );

  const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
  bodyMesh.position.y = 0.15; // half the body height
  rocketGroup.add(bodyMesh);

  // Rocket nose - improved cone with better proportions
  const noseGeometry = new THREE.ConeGeometry(
    0.05, // radius
    0.12, // height - slightly longer
    16, // radial segments
    2, // height segments
    false // open-ended
  );

  const noseMesh = new THREE.Mesh(noseGeometry, bodyMaterial);
  noseMesh.position.y = 0.31; // positioned on top of body
  rocketGroup.add(noseMesh);

  // Add details to the body

  // Body rings
  const addBodyRing = (y: number, scale = 1) => {
    const ringGeometry = new THREE.TorusGeometry(
      0.065 * scale, // ring radius
      0.01, // tube radius
      8, // radial segments
      16 // tubular segments
    );

    const ring = new THREE.Mesh(ringGeometry, detailMaterial);
    ring.rotation.x = Math.PI / 2; // lay flat
    ring.position.y = y;
    rocketGroup.add(ring);
  };

  // Add rings at different positions
  addBodyRing(0.02, 1.03);
  addBodyRing(0.28, 0.9);

  // Improved fins - add 3 fins for more realistic rocket design
  const createFin = () => {
    // Create a custom shape for the fin
    const finShape = new THREE.Shape();
    finShape.moveTo(0, 0);
    finShape.lineTo(0.12, 0); // horizontal extension
    finShape.lineTo(0.08, 0.18); // angled back
    finShape.lineTo(0, 0.15); // back to base
    finShape.lineTo(0, 0); // close shape

    const finGeometry = new THREE.ExtrudeGeometry(finShape, {
      depth: 0.02,
      bevelEnabled: true,
      bevelSegments: 2,
      bevelSize: 0.005,
      bevelThickness: 0.005,
    });

    const fin = new THREE.Mesh(finGeometry, detailMaterial);
    fin.position.set(-0.01, 0, 0); // center on rocket body
    return fin;
  };

  // Add 3 fins evenly spaced around the rocket
  for (let i = 0; i < 3; i++) {
    const fin = createFin();
    fin.position.y = 0.02; // at the bottom of the rocket
    fin.rotation.y = (i * Math.PI * 2) / 3; // evenly spaced
    rocketGroup.add(fin);
  }

  // Add engine exhaust port
  const exhaustPortGeometry = new THREE.CylinderGeometry(
    0.03, // top radius
    0.04, // bottom radius
    0.03, // height
    12, // radial segments
    1, // height segments
    true // open-ended
  );

  const exhaustPortMaterial = new THREE.MeshStandardMaterial({
    color: 0x333333,
    emissive: 0x220000,
    roughness: 0.8,
    metalness: 0.2,
    side: THREE.DoubleSide,
  });

  const exhaustPort = new THREE.Mesh(exhaustPortGeometry, exhaustPortMaterial);
  exhaustPort.position.y = -0.015;
  rocketGroup.add(exhaustPort);

  // Add internal engine glow
  const engineGlowGeometry = new THREE.SphereGeometry(0.02, 12, 12);
  const engineGlowMaterial = new THREE.MeshBasicMaterial({
    color: 0xff3300,
    transparent: true,
    opacity: 0.8,
  });

  const engineGlow = new THREE.Mesh(engineGlowGeometry, engineGlowMaterial);
  engineGlow.position.y = -0.01;
  rocketGroup.add(engineGlow);

  // Add point light for engine
  const engineLight = new THREE.PointLight(0xff5500, 1, 0.5);
  engineLight.position.y = -0.02;
  rocketGroup.add(engineLight);

  // Add glowing effect around the rocket
  const glowGeometry = new THREE.SphereGeometry(0.15, 16, 16);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: VISUAL_CONFIG.COLORS.ROCKET_EMISSIVE,
    transparent: true,
    opacity: 0.15,
    side: THREE.BackSide,
  });

  const glow = new THREE.Mesh(glowGeometry, glowMaterial);
  rocketGroup.add(glow);

  // Store animation parameters
  rocketGroup.userData = {
    rotationSpeed: 0.005,
    hoverOffset: Math.random() * Math.PI * 2,
    hoverSpeed: 0.003,
    hoverHeight: 0.05,
    glowPulseSpeed: 0.01,
    engineFlickerSpeed: 0.05,
    engineGlow: engineGlow,
    engineLight: engineLight,
    glow: glow,
  };

  // Position the rocket in the world
  rocketGroup.position.set(x, 0.05, y);
  scene.add(rocketGroup);

  return rocketGroup;
}

/**
 * Creates a smaller rocket indicator for player
 */
export function createRocketIndicator(): THREE.Group {
  const rocket = new THREE.Group();

  // Body
  const bodyGeometry = new THREE.CylinderGeometry(0.04, 0.05, 0.2, 12);
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: VISUAL_CONFIG.COLORS.ROCKET,
    emissive: VISUAL_CONFIG.COLORS.ROCKET_EMISSIVE,
    emissiveIntensity: 0.8,
    roughness: 0.3,
    metalness: 0.7,
  });

  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  rocket.add(body);

  // Nose
  const noseGeometry = new THREE.ConeGeometry(0.04, 0.1, 12);
  const nose = new THREE.Mesh(noseGeometry, bodyMaterial);
  nose.position.y = 0.15;
  rocket.add(nose);

  // Fins - simplified for the indicator
  const finGeometry = new THREE.BoxGeometry(0.02, 0.08, 0.06);
  const finMaterial = new THREE.MeshStandardMaterial({
    color: 0x888888,
    roughness: 0.4,
    metalness: 0.6,
  });

  // Add 3 fins evenly spaced
  for (let i = 0; i < 3; i++) {
    const fin = new THREE.Mesh(finGeometry, finMaterial);
    fin.position.y = -0.06;
    const angle = (i * Math.PI * 2) / 3;
    fin.position.x = Math.sin(angle) * 0.06;
    fin.position.z = Math.cos(angle) * 0.06;
    fin.rotation.y = -angle;
    rocket.add(fin);
  }

  // Add small engine glow
  const glowGeometry = new THREE.SphereGeometry(0.02, 8, 8);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0xff3300,
    transparent: true,
    opacity: 0.7,
  });

  const glow = new THREE.Mesh(glowGeometry, glowMaterial);
  glow.position.y = -0.1;
  rocket.add(glow);

  // Add a small light
  const light = new THREE.PointLight(0xff5500, 0.7, 0.3);
  light.position.y = -0.1;
  rocket.add(light);

  // Rotate to point downward
  rocket.rotation.x = Math.PI;

  return rocket;
}

/**
 * Create rocket projectile effect with enhanced visuals
 */
export function createRocketEffect(
  x: number,
  y: number,
  direction: number // 0: North, 1: East, 2: South, 3: West
): void {
  // Create a group for the rocket and effects
  const rocketGroup = new THREE.Group();

  // Create the rocket body
  const bodyGeometry = new THREE.CylinderGeometry(
    0.05, // top radius
    0.08, // bottom radius
    0.25, // height
    12, // radial segments
    1, // height segments
    false // closed ended
  );

  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: 0xdd3311,
    emissive: 0xff5500,
    emissiveIntensity: 0.7,
    roughness: 0.3,
    metalness: 0.7,
  });

  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  rocketGroup.add(body);

  // Add nose cone
  const noseGeometry = new THREE.ConeGeometry(0.05, 0.15, 12);
  const nose = new THREE.Mesh(noseGeometry, bodyMaterial);
  nose.position.y = 0.2;
  rocketGroup.add(nose);

  // Add fins
  const finGeometry = new THREE.BoxGeometry(0.02, 0.1, 0.1);
  const finMaterial = new THREE.MeshStandardMaterial({
    color: 0x888888,
    roughness: 0.4,
    metalness: 0.6,
  });

  // Add 3 fins
  for (let i = 0; i < 3; i++) {
    const fin = new THREE.Mesh(finGeometry, finMaterial);
    fin.position.y = -0.12;
    const angle = (i * Math.PI * 2) / 3;
    fin.position.x = Math.sin(angle) * 0.07;
    fin.position.z = Math.cos(angle) * 0.07;
    fin.rotation.y = -angle;
    rocketGroup.add(fin);
  }

  // Add engine flame
  const flameGeometry = new THREE.ConeGeometry(0.06, 0.3, 12, 1, true);
  const flameMaterial = new THREE.MeshBasicMaterial({
    color: 0xff9500,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
  });

  const flame = new THREE.Mesh(flameGeometry, flameMaterial);
  flame.position.y = -0.27;
  flame.rotation.x = Math.PI; // Point downward
  rocketGroup.add(flame);

  // Add inner flame
  const innerFlameGeometry = new THREE.ConeGeometry(0.03, 0.2, 12, 1, true);
  const innerFlameMaterial = new THREE.MeshBasicMaterial({
    color: 0xffff00,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
  });

  const innerFlame = new THREE.Mesh(innerFlameGeometry, innerFlameMaterial);
  innerFlame.position.y = -0.22;
  innerFlame.rotation.x = Math.PI;
  rocketGroup.add(innerFlame);

  // Add point light for the engine
  const engineLight = new THREE.PointLight(0xff5500, 2, 1.5);
  engineLight.position.y = -0.2;
  rocketGroup.add(engineLight);

  // Set up the rocket position and rotation based on direction
  rocketGroup.position.set(x, 0.4, y);

  // Orient the rocket based on direction
  switch (direction) {
    case 0: // North
      rocketGroup.rotation.z = -Math.PI / 2;
      break;
    case 1: // East
      rocketGroup.rotation.x = Math.PI / 2;
      break;
    case 2: // South
      rocketGroup.rotation.z = Math.PI / 2;
      break;
    case 3: // West
      rocketGroup.rotation.x = -Math.PI / 2;
      break;
  }

  scene.add(rocketGroup);

  // Create a group for trail particles
  const trailGroup = new THREE.Group();
  scene.add(trailGroup);

  // Animation variables
  let frameCount = 0;
  const maxFrames = 100;
  let isAnimating = true;

  // Calculate velocity based on direction
  const speed = 0.15; // Increased speed for more dramatic effect
  const velocity = new THREE.Vector3();

  switch (direction) {
    case 0: // North
      velocity.set(0, 0, -speed);
      break;
    case 1: // East
      velocity.set(speed, 0, 0);
      break;
    case 2: // South
      velocity.set(0, 0, speed);
      break;
    case 3: // West
      velocity.set(-speed, 0, 0);
      break;
  }

  // Create trail particles function
  const createTrailParticle = () => {
    // Randomly choose particle type
    let geometry: THREE.BufferGeometry;
    const particleType = Math.random();

    if (particleType > 0.7) {
      // Smoke puff
      geometry = new THREE.SphereGeometry(0.05 + Math.random() * 0.08, 8, 8);
    } else if (particleType > 0.4) {
      // Spark
      geometry = new THREE.TetrahedronGeometry(0.04 + Math.random() * 0.04);
    } else {
      // Fire bit
      geometry = new THREE.BoxGeometry(
        0.03 + Math.random() * 0.03,
        0.03 + Math.random() * 0.03,
        0.03 + Math.random() * 0.03
      );
    }

    // Choose color based on type
    let color: THREE.Color;
    if (particleType > 0.7) {
      // Smoke - dark gray to light gray
      const grayness = 0.3 + Math.random() * 0.4;
      color = new THREE.Color(grayness, grayness, grayness);
    } else if (particleType > 0.4) {
      // Spark - yellow to white
      color = new THREE.Color(
        1,
        0.8 + Math.random() * 0.2,
        0.3 + Math.random() * 0.7
      );
    } else {
      // Fire - red to orange
      color = new THREE.Color(
        1,
        0.3 + Math.random() * 0.5,
        Math.random() * 0.2
      );
    }

    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.7 + Math.random() * 0.3,
    });

    const particle = new THREE.Mesh(geometry, material);

    // Set initial position with some randomness
    const posOffset = new THREE.Vector3(
      (Math.random() - 0.5) * 0.1,
      (Math.random() - 0.5) * 0.1,
      (Math.random() - 0.5) * 0.1
    );

    // Offset more along the trail axis
    switch (direction) {
      case 0: // North
        posOffset.z += 0.15;
        break;
      case 1: // East
        posOffset.x -= 0.15;
        break;
      case 2: // South
        posOffset.z -= 0.15;
        break;
      case 3: // West
        posOffset.x += 0.15;
        break;
    }

    // Position behind the rocket
    particle.position.copy(rocketGroup.position).add(posOffset);

    // Add random velocity component
    const randomVel = new THREE.Vector3(
      (Math.random() - 0.5) * 0.05,
      (Math.random() - 0.5) * 0.05 + 0.01, // Slight upward bias
      (Math.random() - 0.5) * 0.05
    );

    // Store particle data
    particle.userData = {
      velocity: randomVel,
      age: 0,
      maxAge: 15 + Math.random() * 20,
      type: particleType, // Store type for animation
    };

    trailGroup.add(particle);
    return particle;
  };

  // Animation function
  function animateRocket() {
    if (!isAnimating || frameCount > maxFrames) {
      scene.remove(rocketGroup);
      scene.remove(trailGroup);
      return;
    }

    // Update rocket position
    rocketGroup.position.add(velocity);

    // Create trail particles
    if (frameCount % 2 === 0) {
      // Create 1-3 particles at once for denser effect
      const particleCount = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < particleCount; i++) {
        createTrailParticle();
      }
    }

    // Animate flame
    if (flame && innerFlame) {
      // Flicker effect
      const flicker = 0.9 + Math.random() * 0.2;
      flame.scale.set(flicker, 1 + Math.random() * 0.2, flicker);
      innerFlame.scale.set(flicker, 1 + Math.random() * 0.3, flicker);

      // Color shift
      if (Math.random() > 0.7) {
        (innerFlameMaterial as THREE.MeshBasicMaterial).color.setHSL(
          0.05 + Math.random() * 0.05, // orange-yellow hue
          1,
          0.5 + Math.random() * 0.5 // varying brightness
        );
      }
    }

    // Update trail particles
    const toRemove: number[] = [];
    for (let i = 0; i < trailGroup.children.length; i++) {
      const particle = trailGroup.children[i];
      particle.userData.age++;

      if (particle.userData.age < particle.userData.maxAge) {
        // Update position
        particle.position.add(particle.userData.velocity);

        // Apply "gravity" to velocity
        particle.userData.velocity.y -= 0.001;

        // Slow down over time
        particle.userData.velocity.multiplyScalar(0.98);

        // Fade out
        const fadeRatio = 1 - particle.userData.age / particle.userData.maxAge;
        ((particle as THREE.Mesh).material as THREE.Material).opacity =
          fadeRatio * 0.7;

        // Grow smoke, shrink fire/sparks
        if (particle.userData.type > 0.7) {
          // Smoke grows
          particle.scale.multiplyScalar(1.01);
        } else {
          // Fire/sparks shrink
          particle.scale.multiplyScalar(0.98);
        }

        // Add rotation
        particle.rotation.x += 0.02;
        particle.rotation.y += 0.02;
        particle.rotation.z += 0.02;
      } else {
        toRemove.push(i);
      }
    }

    // Remove expired particles
    for (let i = toRemove.length - 1; i >= 0; i--) {
      trailGroup.remove(trailGroup.children[toRemove[i]]);
    }

    frameCount++;
    requestAnimationFrame(animateRocket);
  }

  // Start animation
  animateRocket();

  // Safety cleanup timeout
  setTimeout(() => {
    if (isAnimating) {
      isAnimating = false;
      scene.remove(rocketGroup);
      scene.remove(trailGroup);
    }
  }, 5000);
}

/**
 * Animates a rocket pickup
 */
export function animateRocket(rocketMesh: THREE.Group, time: number): void {
  if (!rocketMesh || !rocketMesh.userData) return;

  const ud = rocketMesh.userData;

  // Hovering effect
  const hoverOffset =
    Math.sin(time * ud.hoverSpeed + ud.hoverOffset) * ud.hoverHeight;
  rocketMesh.position.y = 0.05 + hoverOffset;

  // Gentle rotation
  rocketMesh.rotation.y += ud.rotationSpeed;

  // Animate glow
  if (ud.glow?.material) {
    const material = ud.glow.material as THREE.MeshBasicMaterial;
    material.opacity = 0.15 + Math.sin(time * ud.glowPulseSpeed) * 0.05;
  }

  // Animate engine effects
  if (ud.engineGlow?.material) {
    const material = ud.engineGlow.material as THREE.MeshBasicMaterial;
    material.opacity = 0.7 + Math.random() * 0.3; // Random flicker
  }

  if (ud.engineLight) {
    ud.engineLight.intensity = 0.8 + Math.random() * 0.4; // Random intensity
  }
}
